'use client';

import EmailInput from '@/components/app-ui/form/EmailInput';
import PasswordInput from '@/components/app-ui/form/PasswordInput';
import SubmitButton from '@/components/app-ui/form/SubmitButton';
import TextInput from '@/components/app-ui/form/TextInput';
import { SignupDto } from '@/dtos/auth.dto';
import userApi from '@/services/user-api.service';
import { signIn } from 'next-auth/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { INPUT_LIMITS, LEAD_SOURCES, PROFESSIONS, STUDENT_DEVICE_USAGE } from '@/constants/form.constants';
import CustomSelect from '@/components/app-ui/form/CustomSelect';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { WELCOME_ROUTE } from '@/constants/ui.constants';
import { getStepResolver } from './signupValidation';
import { gtmService } from '@/services/gtm.service';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import toast from 'react-hot-toast';

export interface CredentialsSignupFormData {
	firstName: string;
	lastName: string;
	email: string;
	password?: string;
	organization: string;
	profession: string;
	leadSource: string;
	studentDeviceUsage: string;
}

export default function CredentialsSignupForm({ signupEmail }: { signupEmail?: string }) {
	const router = useRouter();

	const [step, setStep] = useState(1);
	const [isSigningUp, setIsSigningUp] = useState(false);

	const credentialsSignupForm = useForm<CredentialsSignupFormData>({
		defaultValues: {
			firstName: '',
			lastName: '',
			email: signupEmail || '',
			password: '',
			organization: '',
			profession: '',
			leadSource: '',
			studentDeviceUsage: '',
		},
		mode: 'onSubmit',
		resolver: getStepResolver(step),
	});
	const { handleSubmit, formState, control, setValue, setError } = credentialsSignupForm;
	const { errors } = formState;

	// const { executeRecaptcha } = useGoogleReCaptcha();

	const submitForm = async (data: CredentialsSignupFormData) => {
		if (step === 1) {
			const userExists = await userApi.checkIfUserExists(data.email);
			if (userExists) return setError('email', { message: 'Email exists, please login instead' });
			setStep(2);
		} else {
			// console.log('Form inputs', data);

			// if (!executeRecaptcha) {
			// 	toast.error('Recaptcha not loaded. Please refresh the page and try again.');
			// 	return;
			// }

			// setIsSigningUp(true);
			// const token = await executeRecaptcha('credentials_signup');
			// if (!token) {
			// 	setIsSigningUp(false);
			// 	toast.error('Recaptcha not loaded. Please refresh the page and try again.');
			// 	return;
			// }

			setIsSigningUp(true);
			const signupDto: SignupDto = {
				firstName: data.firstName.trim(),
				lastName: data.lastName.trim(),
				email: data.email.trim().toLowerCase(),
				organization: data.organization.trim(),
				jobRole: data.profession,
				leadSource: data.leadSource,
				signupSurvey: {
					studentDeviceUsage: data.studentDeviceUsage,
				},
				token: '',
			};
			if (data.password) signupDto.password = data.password.trim();

			// console.log('signupDto', signupDto);
			const userId = await userApi.createUser(signupDto);
			if (!userId) {
				router.push('/auth/error');
				return;
			} else {
				if (!!data.email && !!data.password) {
					signIn('credentials', {
						email: data.email,
						password: data.password,
						callbackUrl: WELCOME_ROUTE,
					});
					gtmService.sendSignUpEvent(userId, 'credentials');
					return;
				} else {
					router.push('/auth/error');
					return;
				}
			}
		}
	};

	return (
		<>
			<form noValidate onSubmit={handleSubmit(submitForm)}>
				<div className="mx-auto max-w-xl space-y-3 lg:mr-0 lg:max-w-lg">
					{step === 1 && (
						<>
							<div className="grid grid-cols-1 gap-x-4 space-y-3 xxs:gap-x-6 xs:grid-cols-2 xs:space-y-0">
								<TextInput
									name="firstName"
									label="First name"
									maxLength={INPUT_LIMITS.FIRST_LAST_NAME_MAX}
									control={control}
									errorMessage={errors.firstName?.message}
								/>
								<TextInput
									name="lastName"
									label="Last name"
									maxLength={INPUT_LIMITS.FIRST_LAST_NAME_MAX}
									control={control}
									errorMessage={errors.lastName?.message}
								/>
							</div>
							<EmailInput
								name="email"
								label="Email"
								control={control}
								errorMessage={errors.email?.message}
							/>
							<PasswordInput
								name="password"
								label="Password"
								maxLength={INPUT_LIMITS.PASSWORD_MAX}
								control={control}
								errorMessage={errors.password?.message}
							/>
							<TextInput
								name="organization"
								label="Organization"
								maxLength={INPUT_LIMITS.ORGANIZATION_MAX}
								control={control}
								errorMessage={errors.organization?.message}
							/>
						</>
					)}
					{step === 2 && (
						<>
							<CustomSelect
								name="profession"
								label="Your profession"
								options={PROFESSIONS}
								defaultValue={PROFESSIONS[0]}
								control={control}
								errorMessage={errors.profession?.message}
							/>
							<CustomSelect
								name="leadSource"
								label="How did you hear about ClassPoint?"
								options={LEAD_SOURCES.slice(1).sort(() => Math.random() - 0.5)}
								defaultValue={LEAD_SOURCES[0]}
								control={control}
								errorMessage={errors.leadSource?.message}
							/>
							<CustomSelect
								name="studentDeviceUsage"
								label="Use of student devices in your classroom"
								options={STUDENT_DEVICE_USAGE}
								defaultValue={STUDENT_DEVICE_USAGE[0]}
								control={control}
								errorMessage={errors.studentDeviceUsage?.message}
							/>
						</>
					)}
				</div>
				<div className="mt-6 space-y-3">
					<SubmitButton fullWidth isSubmitting={isSigningUp}>
						{step === 1 ? 'Next' : 'Finish signing up'}
					</SubmitButton>
					{step === 2 && (
						<Button
							variant={'secondary'}
							className="w-full"
							disabled={isSigningUp}
							onClick={() => {
								setStep(1);
								setValue('profession', '');
								setValue('leadSource', '');
								setValue('studentDeviceUsage', '');
							}}
						>
							Back
						</Button>
					)}
				</div>
			</form>
		</>
	);
}
